import { FILING_MODE } from "../enums/filingMode";
import { MedicalPaOrdersAuthStatus } from "../enums/medicalPaOrdersAuthStatus";

/**
 * Title-cases every underscore- or hyphen-separated segment of a filing mode.
 *
 * @param filingMode - Raw filing-mode string (e.g. `not_applicable`)
 * @returns Capitalized display string (e.g. `Not Applicable`)
 */
const capitalizeFilingMode = (filingMode: string): string =>
  filingMode
    .split(/[_-]+/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

/**
 * Resolves the display value for a Filing Info cell.
 *
 * Saved `filingMode` / `filingDetail` from the backend take priority. Portal
 * shows `filingDetail` when it is non-empty, or "-" if it is missing. Every
 * other mode shows the capitalized `filingMode`. When mode is empty and the
 * auth status is `no_auth_required`, falls back to "Not Applicable"; otherwise
 * returns "-".
 *
 * @param authStatusId - Resolved auth-status id for the J-code/order
 * @param filingMode - Saved filing mode (portal, fax, call, not_applicable)
 * @param filingDetail - Portal name or fax number when the mode needs one
 * @returns Cell display string
 */
export const getFilingInfoValueForDisplay = (
  authStatusId: string | undefined,
  filingMode: string | undefined,
  filingDetail: string | undefined,
): string => {
  const trimmedMode = filingMode?.trim() ?? "";
  if (trimmedMode) {
    if (trimmedMode === FILING_MODE.PORTAL) {
      const trimmedDetail = filingDetail?.trim() ?? "";
      return trimmedDetail.length > 0 ? trimmedDetail : "-";
    }

    return capitalizeFilingMode(trimmedMode);
  }

  if (authStatusId === MedicalPaOrdersAuthStatus.NoAuthRequired) {
    return "Not Applicable";
  }

  return "-";
};

/**
 * Resolves the raw filing mode stored on a CPT row (`filingInfo.value`).
 *
 * Unlike {@link getFilingInfoValueForDisplay}, this returns the persisted
 * mode id (e.g. `portal`, `fax`, `not_applicable`) or an empty string when
 * unset. No-auth-required rows default to {@link FILING_MODE.NOT_APPLICABLE}.
 *
 * @param authStatusId - Resolved auth-status id for the J-code/order
 * @param filingMode - Saved filing mode from the backend
 * @returns Raw filing-mode string for row state and API payloads
 */
export const getRawFilingInfoValue = (
  authStatusId: string | undefined,
  filingMode: string | undefined,
): string => {
  const trimmedMode = filingMode?.trim() ?? "";
  if (trimmedMode.length > 0) {
    return trimmedMode;
  }

  if (authStatusId === MedicalPaOrdersAuthStatus.NoAuthRequired) {
    return FILING_MODE.NOT_APPLICABLE;
  }

  return "";
};

/**
 * Resolves the raw filing detail stored on a CPT row (`filingInfo.detail`).
 *
 * Unlike a trim-only helper, this matches {@link getRawFilingInfoValue}: a
 * non-empty saved detail wins, no-auth-required rows default to
 * {@link FILING_MODE.NOT_APPLICABLE}, and every other unset case stays empty.
 *
 * @param authStatusId - Resolved auth-status id for the J-code/order
 * @param filingDetail - Saved portal name or fax number from the backend
 * @returns Raw filing-detail string for row state and API payloads
 */
export const getFilingInfoDetail = (
  authStatusId: string | undefined,
  filingDetail: string | undefined,
): string => {
  const trimmedDetail = filingDetail?.trim() ?? "";
  if (trimmedDetail.length > 0) {
    return trimmedDetail;
  }

  if (authStatusId === MedicalPaOrdersAuthStatus.NoAuthRequired) {
    return FILING_MODE.NOT_APPLICABLE;
  }

  return "";
};
